import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, timer, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { LoginRequest, LoginResponse, RefreshTokenRequest, UserInfo } from '../models/auth.model';
import { ApiResponse } from '../models/api-response.model';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { readTokenExpiry, isTokenExpired } from '../shared/utils/jwt-token.util';

/** An auth error that remembers whether the failure was worth ending the session over. */
export interface AuthError extends Error {
  /** True when the request never reached the server, so the session may still be valid. */
  transient?: boolean;
}

/**
 * True when a failure should NOT end the session.
 *
 * Only status 0 — the request never reached the server, or the connection dropped before
 * any response — qualifies. A 5xx does NOT: the backend rotates the refresh token in
 * place (AuthService.cs overwrites TokenHash), so on a lost response the server may
 * already have committed the rotation. Retrying then sends a dead token and earns a 401,
 * which would cause the very logout this is meant to prevent.
 */
function isTransientAuthError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) return error.status === 0;
  return !!(error as AuthError)?.transient;
}

/** Wrap a message as an Error that carries the transient flag forward to callers. */
function toAuthError(message: string, source: any): AuthError {
  const error: AuthError = new Error(message);
  error.transient = source?.status === 0;
  return error;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** How often the session watchdog checks the access token. */
  private static readonly REFRESH_TICK_MS = 60 * 1000;
  /** Refresh once the token is this close to expiring (or already past it). */
  private static readonly REFRESH_WINDOW_MS = 5 * 60 * 1000;
  /** How many connection failures in a row before we stop hoping and say so. */
  private static readonly MAX_REFRESH_ATTEMPTS = 3;

  private baseUrl = `${environment.apiUrl}/auth`;
  private currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private refreshTimer?: any;
  /** Guards the watchdog against stacking refreshes if one is slow. */
  private isRefreshInFlight = false;
  /** Consecutive transient refresh failures. Reset by any successful refresh. */
  private consecutiveRefreshFailures = 0;

  /**
   * Optional on purpose. ToastService needs ngx-toastr, which the app provides globally
   * (app.config.ts) but a TestBed does not. AuthService is pulled in by NavbarComponent,
   * so a required dependency here would force every spec that renders a page to provide
   * ngx-toastr - it broke 26 unrelated DeviceComponent tests before this was made optional.
   */
  private toast = inject(ToastService, { optional: true });

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check if user is already logged in
    this.initializeAuth();
  }
  private initializeAuth(): void {
    const token = this.getAccessToken();
    if (token && !isTokenExpired(token)) {
      // Get user info if token is valid
      this.getCurrentUser().subscribe({
        next: (user) => {
          this.currentUserSubject.next(user);
          this.scheduleTokenRefresh();
        },
        // A flaky connection at boot must not wipe a valid session.
        error: (error) => this.endSessionUnlessTransient(error)
      });
    } else {
      // Try to refresh token if access token is expired
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        this.refreshToken().subscribe({
          next: () => this.scheduleTokenRefresh(),
          error: (error) => this.endSessionUnlessTransient(error)
        });
      }
    }
  }  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<ApiResponse<LoginResponse>>(`${this.baseUrl}/login`, credentials)
      .pipe(
        map(response => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Login failed');
          }

          const loginData = response.data;
          this.setTokens(loginData.accessToken, loginData.refreshToken);
          this.currentUserSubject.next(loginData.user);
          this.scheduleTokenRefresh();
          return loginData;
        }),
        catchError((error) => {
          return this.handleError(error);
        })
      );
  }logout(): Observable<{ success: boolean }> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      // The API requires the refresh token so it can revoke it server-side.
      // Sending an empty body fails [Required] validation → 400 and the token
      // is never revoked.
      return this.http.post<ApiResponse<any>>(`${this.baseUrl}/logout`, { refreshToken })
        .pipe(
          catchError(() => {
            // Even if logout fails on server, clear local tokens
            this.clearTokensAndUserSilent();
            return of({ success: true });
          }),
          map(() => {
            this.clearTokensAndUserSilent();
            return { success: true };
          })
        );
    } else {
      this.clearTokensAndUserSilent();
      return of({ success: true });
    }
  }
  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };
    
    return this.http.post<ApiResponse<LoginResponse>>(`${this.baseUrl}/refresh`, request)
      .pipe(
        map(response => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Token refresh failed');
          }
          
          const loginData = response.data;
          this.consecutiveRefreshFailures = 0;
          this.setTokens(loginData.accessToken, loginData.refreshToken);
          this.currentUserSubject.next(loginData.user);
          this.scheduleTokenRefresh();
          return loginData;
        }),
        // No logout here. This used to call this.logout() AND every caller also handled
        // the error with logout(), so a single blip tried to end the session twice — the
        // second time POSTing with no refresh token. Worse, logout() returns a cold
        // observable that nobody subscribed to, so it never actually cleared anything.
        // Failure handling now belongs to the caller, which can tell a dead token from a
        // dropped connection.
        catchError(error => this.handleError(error))
      );
  }

  getCurrentUser(): Observable<UserInfo> {
    return this.http.get<ApiResponse<UserInfo>>(`${this.baseUrl}/me`)
      .pipe(
        map(response => {
          if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to get user info');
          }
          return response.data;
        }),
        catchError(this.handleError)
      );
  }

  // Token management
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }  private clearTokensAndUser(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.isRefreshInFlight = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  private clearTokensAndUserSilent(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.isRefreshInFlight = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.currentUserSubject.next(null);
  }

  /**
   * Keep the access token fresh with a periodic CHECK, not a one-shot timer.
   *
   * The old form scheduled a single setTimeout for (expiry - 5 min). A one-shot timer is
   * silently lost whenever the machine sleeps or the browser throttles a background tab,
   * and it was re-armed only on its own success — so one missed firing disabled proactive
   * refresh for the rest of the session and left the (previously broken) 401 interceptor
   * as the only safety net. A tick that re-reads the token each time recovers on its own
   * from sleep/wake and from tokens written by the interceptor.
   *
   * This ticks every 60s and refreshes ONLY inside the last 5 minutes of the token's life.
   * It is deliberately not "refresh every 60s": with a 12h token that would rotate the
   * refresh token ~720x per user per day, and every rotation is a database write.
   *
   * Idempotent — callers invoke it after login, after a refresh, and on boot.
   */
  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = setInterval(
      () => this.refreshIfExpiring(),
      AuthService.REFRESH_TICK_MS
    );
  }

  /** One watchdog tick: refresh only if the token is about to expire or already has. */
  private refreshIfExpiring(): void {
    const token = this.getAccessToken();
    if (!token) return;

    const expiresAt = readTokenExpiry(token);
    if (expiresAt === null) {
      // Previously swallowed in silence, which disabled refresh forever. A token we
      // cannot parse is unusable, but tearing the session down from a background timer
      // would be worse than letting the next request 401 into the interceptor's refresh.
      console.error('[auth] access token could not be parsed; leaving recovery to the 401 handler');
      return;
    }

    if (Date.now() < expiresAt - AuthService.REFRESH_WINDOW_MS) return;
    if (this.isRefreshInFlight) return;

    this.isRefreshInFlight = true;
    this.refreshToken().subscribe({
      next: () => { this.isRefreshInFlight = false; },
      error: (error) => {
        this.isRefreshInFlight = false;
        this.endSessionUnlessTransient(error);
      }
    });
  }

  /**
   * Decide whether a failed refresh should end the session.
   *
   * A dropped connection must not: on a warehouse machine with flaky wifi that would
   * bounce the user to /login mid-task, which is the bug this work exists to fix. The
   * watchdog simply tries again on its next tick.
   *
   * But "keep trying" cannot mean "forever, silently". If the backend is down, every
   * request 401s while the watchdog retries in the background and the user is left
   * clicking a dead screen with no idea why. After MAX_REFRESH_ATTEMPTS consecutive
   * connection failures we stop and say so.
   *
   * Anything that is NOT a connection failure (a 401 on a dead refresh token, a 400 on a
   * malformed one) ends the session immediately — retrying those can never succeed.
   */
  private endSessionUnlessTransient(error: unknown): void {
    if (!isTransientAuthError(error)) {
      this.endSession();
      return;
    }

    this.consecutiveRefreshFailures++;
    if (this.consecutiveRefreshFailures >= AuthService.MAX_REFRESH_ATTEMPTS) {
      this.toast?.error(
        "We couldn't renew your session — check your connection and sign in again.",
        'Session expired'
      );
      this.endSession();
    }
  }

  private endSession(): void {
    this.consecutiveRefreshFailures = 0;
    this.logout().subscribe();
    this.router.navigate(['/login']);
  }

  // Utility methods
  isLoggedIn(): boolean {
    const token = this.getAccessToken();
    return token != null && !isTokenExpired(token);
  }

  /**
   * True when this browser still has something to authenticate with — a currently valid
   * access token, OR a refresh token that can mint one.
   *
   * Route guards use this rather than isLoggedIn(): the access token lapses long before
   * the session does (the refresh token lasts days), so gating navigation on isLoggedIn()
   * bounced people to /login for a session the server would happily have renewed. The
   * first API call on the restored route drives the actual refresh, and a genuinely dead
   * session still lands on /login via the interceptor's 401 path.
   *
   * Note it is NOT "an access token exists, valid or not" — that alone would admit
   * someone whose refresh token is gone into a page where every request 401s, which is a
   * worse experience than a clean redirect.
   */
  hasSession(): boolean {
    return this.isLoggedIn() || this.getRefreshToken() != null;
  }

  getCurrentUserValue(): UserInfo | null {
    return this.currentUserSubject.value;
  }

  getUserRole(): string | null {
    const user = this.getCurrentUserValue();
    // Fall back to the JWT's role claim so role guards stay correct on a hard
    // refresh, before initializeAuth()'s /auth/me call has populated the user.
    return user ? user.role : this.getRoleFromToken();
  }

  /** Read the role claim directly from the access token (synchronous). */
  private getRoleFromToken(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Backend emits the role via ClaimTypes.Role, which serializes to the
      // long WS-Identity URI unless shortened; accept either form.
      return payload.role
        ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
        ?? null;
    } catch {
      return null;
    }
  }

  hasRole(role: string): boolean {
    const userRole = this.getUserRole();
    return userRole === role;
  }

  hasAnyRole(roles: string[]): boolean {
    const userRole = this.getUserRole();
    return !!userRole && roles.includes(userRole);
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    // Handle standardized API error responses
    if (error.error && typeof error.error === 'object') {
      const apiError = error.error as ApiResponse;
      if (apiError.message) {
        errorMessage = apiError.message;
        
        // Include validation errors if present
        if (apiError.errors) {
          if (Array.isArray(apiError.errors)) {
            errorMessage += ': ' + apiError.errors.join(', ');
          } else if (typeof apiError.errors === 'object') {
            const validationErrors = Object.values(apiError.errors).flat();
            errorMessage += ': ' + validationErrors.join(', ');
          }
        }
      }
    } else if (error.status) {
      // Handle HTTP status codes
      switch (error.status) {
        case 400:
          errorMessage = 'Invalid request. Please check your input and try again.';
          break;
        case 401:
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          break;
        case 403:
          errorMessage = 'Access denied. Your account may be disabled or you do not have permission to access this system.';
          break;
        case 404:
          errorMessage = 'Authentication service not found. Please contact support.';
          break;
        case 422:
          errorMessage = 'Invalid email or password format. Please check your input.';
          break;
        case 429:
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later or contact support if the problem persists.';
          break;
        case 502:
        case 503:
        case 504:
          errorMessage = 'Service temporarily unavailable. Please try again in a few moments.';
          break;
        case 0:
          errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
          break;
        default:
          errorMessage = `Connection error (${error.status}). Please try again later.`;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Stays free of `this` on purpose — getCurrentUser() passes this method unbound as
    // catchError(this.handleError), so any instance access here would throw at runtime.
    return throwError(() => toAuthError(errorMessage, error));
  }
}
